// client-side imagekit upload helpers shared across browser upload flows.
// wraps auth fetch, svg sanitization, retry, and optional folder/privacy settings.
// does not save images to the website library or run server-side rename — callers
// handle post-upload steps. depends on: imagekit-javascript, @/lib/network, @/types/imagekit.
//
// ## Which helper to use (browser only)
//
// | Helper | Use when |
// |--------|----------|
// | uploadEditorContentImage | Rich-text editors (tiptap). Uploads one file and returns a CDN URL with the standard content transform (w-1000) ready to embed in HTML. |
// | uploadClientFile | You need custom ImageKit options: folder path, private files, or a non-default size check. Example: quote/invoice attachments. |
// | uploadClientImage | Single image with default photo settings. You handle the UploadResponse (save to API, show preview, etc.). |
// | getTransformedImageKitUrl | You already have an ImageKit filePath and only need the transformed CDN URL. |
//
// Related exports in @/lib/images (same module, different entry points):
// - uploadMultipleImages — several files in one flow; fetches one auth token per file. Used by embed forms, jobs photos, bulk pickers.
// - uploadSingleImage — low-level; caller already has an auth token (used internally by uploadMultipleImages).
//
// Do NOT use this module when:
// - You need the ImageKit upload widget + photo-library save/rename → use useImageKitUpload hook.
// - Upload runs on the server (API route, Inngest) → use @/lib/imagekit server client.

import ImageKit from "imagekit-javascript";

import { MAX_FILE_SIZE_MB } from "@/lib/constants/photos";
import { requestWithRetry } from "@/lib/network";
import { api } from "@/lib/utils";
import { sanitizeIfSVG } from "@/lib/utils/svgSanitizer";

import type { ImagekitAuth } from "@/types/imagekit";
import type { UploadResponse } from "imagekit-javascript/dist/src/interfaces";

const IK_PUBLIC_KEY = process.env.NEXT_PUBLIC_IMAGE_KIT_PUBLIC;

/** Default transform for editor and general content images (matches server uploadImage). */
export const CONTENT_IMAGE_TRANSFORMATION = "tr:w-1000,f-auto,lo-true,q-80";

type ImagekitUploadFile = File | Blob;

const imagekit = new ImageKit({
  publicKey: IK_PUBLIC_KEY!,
  urlEndpoint: process.env.NEXT_PUBLIC_IMAGE_KIT_ENDPOINT!,
});

export type ClientImageKitUploadOptions = {
  fileName?: string;
  useUniqueFileName?: boolean;
  folder?: string;
  isPrivateFile?: boolean;
  checks?: string;
  /** When set, /api/imagekit/auth validates access to this website before issuing tokens. */
  websiteId?: string;
};

async function getImagekitAuth(count: number, websiteId?: string): Promise<ImagekitAuth[]> {
  const params: { count: number; websiteId?: string } = { count };
  if (websiteId) {
    params.websiteId = websiteId;
  }

  const res = await requestWithRetry(() =>
    api.get<ImagekitAuth[]>("/api/imagekit/auth", {
      params,
    })
  );
  return res.data;
}

export function deriveDefaultFileName(processedFile: ImagekitUploadFile): string {
  if (processedFile instanceof File) {
    const extension = processedFile.name.split(".").pop()?.toLowerCase();
    if (extension === "svg") {
      return "image.svg";
    }
    if (["png", "jpg", "jpeg", "gif", "webp"].includes(extension || "")) {
      return `image.${extension}`;
    }
  }
  return "image.webp";
}

/**
 * Upload one file from the browser with configurable ImageKit options.
 * Fetches a fresh auth token, sanitizes SVGs, and retries on transient failures.
 *
 * Use for CRM attachments, or any flow that needs a custom folder, private files,
 * or a size limit other than the default photo MAX_FILE_SIZE_MB.
 * Returns null on failure (auth missing, upload error). Does not toast or log.
 */
export async function uploadClientFile(
  imageFile: ImagekitUploadFile,
  options: ClientImageKitUploadOptions = {}
): Promise<UploadResponse | null> {
  try {
    const processedFile = await sanitizeIfSVG(imageFile);
    const fileName = options.fileName ?? deriveDefaultFileName(processedFile);
    const checks = options.checks ?? `"file.size" <= "${MAX_FILE_SIZE_MB}MB"`;

    // Auth is fetched inside the retry boundary so each attempt uses a fresh token.
    return await requestWithRetry(async () => {
      const [auth] = await getImagekitAuth(1, options.websiteId);
      if (!auth) {
        return null;
      }

      return imagekit.upload({
        file: processedFile,
        fileName,
        useUniqueFileName: options.useUniqueFileName ?? true,
        folder: options.folder,
        isPrivateFile: options.isPrivateFile,
        signature: auth.signature,
        expire: auth.expire,
        token: auth.token,
        checks,
      });
    });
  } catch {
    return null;
  }
}

/**
 * Upload one image with default photo settings (unique file name, MAX_FILE_SIZE_MB check).
 *
 * Use when you need the raw ImageKit UploadResponse and will handle persistence yourself
 * (e.g. pass fileId/filePath to a follow-up API call). Prefer uploadMultipleImages for
 * several files so auth tokens are batched in one request.
 */
export async function uploadClientImage(imageFile: ImagekitUploadFile): Promise<UploadResponse | null> {
  return uploadClientFile(imageFile);
}

/**
 * Build a public ImageKit CDN URL with an optional transformation segment.
 * Pure function — no network I/O. Strips a leading slash from filePath.
 */
export function getTransformedImageKitUrl(
  filePath: string,
  transformation: string = CONTENT_IMAGE_TRANSFORMATION
): string {
  const endpoint = process.env.NEXT_PUBLIC_IMAGE_KIT_ENDPOINT;
  if (!endpoint) {
    throw new Error(
      "ImageKit configuration is missing. Please check NEXT_PUBLIC_IMAGE_KIT_ENDPOINT environment variable."
    );
  }

  const fileName = filePath.replace(/^\//, "");
  return `${endpoint}/${transformation}/${fileName}`;
}

/**
 * Upload a rich-text editor image and return a transformed CDN URL for HTML content.
 *
 * Use in tiptap and similar editors where the inserted src must include the standard
 * content transform (w-1000,f-auto,lo-true,q-80). Bypasses the Vercel 4.5 MB body
 * limit because bytes go straight from the browser to ImageKit. Throws on failure.
 */
export async function uploadEditorContentImage(file: File, websiteId: string): Promise<string> {
  const result = await uploadClientFile(file, {
    folder: `/editor-content/${websiteId}`,
    websiteId,
  });
  if (!result?.filePath) {
    throw new Error("Failed to upload image");
  }
  return getTransformedImageKitUrl(result.filePath);
}

export { imagekit, getImagekitAuth };
